import type { Map as MlMap, Marker as MlMarker, MarkerOptions } from 'maplibre-gl';
import { env } from '../../lib/env';
import type { MapTheme } from '../../stores/map-theme';

/** Mapbox GL and MapLibre GL share this API surface; we type against MapLibre. */
export type MapLike = MlMap;
export type MarkerLike = MlMarker;

export interface MapHandle {
  map: MapLike;
  lib: 'mapbox' | 'maplibre';
  createMarker: (opts: MarkerOptions) => MarkerLike;
}

interface CreateOpts {
  container: HTMLElement;
  center: [number, number];
  zoom: number;
  interactive?: boolean;
  theme?: MapTheme;
}

/** Light: colourful streets map (roads, parks, water, places). Dark: the night map. */
const MAPLIBRE_STYLE: Record<MapTheme, string> = {
  light: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  dark: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
};
const MAPBOX_STYLE: Record<MapTheme, string> = {
  light: 'mapbox://styles/mapbox/streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
};

let mapboxFailed = false;

/**
 * Creates the game map with Mapbox GL JS when a token is configured, falling back
 * to MapLibre GL + free CARTO tiles when there is no token or Mapbox rejects it.
 */
export async function createGameMap(opts: CreateOpts): Promise<MapHandle> {
  if (env.mapboxToken && !mapboxFailed) {
    try {
      return await createMapbox(opts);
    } catch (e) {
      console.warn('Mapbox unavailable, falling back to MapLibre', e);
      mapboxFailed = true;
      opts.container.innerHTML = '';
    }
  }
  return createMaplibre(opts);
}

async function createMapbox(opts: CreateOpts): Promise<MapHandle> {
  const [{ default: mapboxgl }] = await Promise.all([
    import('mapbox-gl'),
    import('mapbox-gl/dist/mapbox-gl.css'),
  ]);
  mapboxgl.accessToken = env.mapboxToken;
  const map = new mapboxgl.Map({
    container: opts.container,
    style: MAPBOX_STYLE[opts.theme ?? 'light'],
    center: opts.center,
    zoom: opts.zoom,
    interactive: opts.interactive ?? true,
    attributionControl: true,
    pitchWithRotate: false,
    dragRotate: false,
    projection: 'mercator',
  });
  // Reject quickly if the token is invalid so we can fall back.
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => resolve(), 8000);
    map.once('style.load', () => {
      clearTimeout(timer);
      resolve();
    });
    map.on('error', (e: { error?: { status?: number; message?: string } }) => {
      const status = e.error?.status;
      if (status === 401 || status === 403) {
        clearTimeout(timer);
        map.remove();
        reject(new Error(`Mapbox token rejected (${status})`));
      }
    });
  });
  return {
    map: map as unknown as MapLike,
    lib: 'mapbox',
    createMarker: (o) => new mapboxgl.Marker(o as mapboxgl.MarkerOptions) as unknown as MarkerLike,
  };
}

async function createMaplibre(opts: CreateOpts): Promise<MapHandle> {
  const [maplibre] = await Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')]);
  const map = new maplibre.Map({
    container: opts.container,
    style: MAPLIBRE_STYLE[opts.theme ?? 'light'],
    center: opts.center,
    zoom: opts.zoom,
    interactive: opts.interactive ?? true,
    attributionControl: { compact: true },
    pitchWithRotate: false,
    dragRotate: false,
  });
  await new Promise<void>((resolve) => {
    if (map.isStyleLoaded()) resolve();
    else map.once('load', () => resolve());
    setTimeout(resolve, 8000);
  });
  return { map, lib: 'maplibre', createMarker: (o) => new maplibre.Marker(o) };
}

export async function addNavigationControl(handle: MapHandle) {
  if (handle.lib === 'mapbox') {
    const { default: mapboxgl } = await import('mapbox-gl');
    handle.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }) as never, 'bottom-right');
  } else {
    const maplibre = await import('maplibre-gl');
    handle.map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-right');
  }
}

export function centerOn(handle: MapHandle | null, lat: number, lng: number, zoom?: number) {
  handle?.map.easeTo({ center: [lng, lat], zoom: zoom ?? Math.max(handle.map.getZoom(), 15), duration: 700 });
}

/**
 * A copy of what the map shows right now (tiles and our layers, without the HTML controls).
 * WebGL clears its buffer after each frame, so the pixels are copied inside the next render.
 */
export function snapshotMap(handle: MapHandle): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const m = handle.map;
    const timer = setTimeout(() => reject(new Error('Map did not render')), 5000);
    m.once('render', () => {
      clearTimeout(timer);
      try {
        const src = m.getCanvas();
        const out = document.createElement('canvas');
        out.width = src.width;
        out.height = src.height;
        out.getContext('2d')!.drawImage(src, 0, 0);
        resolve(out);
      } catch (e) {
        reject(e as Error);
      }
    });
    m.triggerRepaint();
  });
}

/** Wait until the map has loaded everything in view (tiles, sources) and is not moving. */
export function mapIdle(handle: MapHandle, timeoutMs = 6000): Promise<void> {
  return new Promise((resolve) => {
    const m = handle.map;
    if (m.loaded() && !m.isMoving()) return resolve();
    const timer = setTimeout(resolve, timeoutMs);
    m.once('idle', () => {
      clearTimeout(timer);
      resolve();
    });
  });
}
